import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { EventFieldPath } from '@/types/field-mapping-flow';
import { useAppStore } from '@/stores/app-store';
import { EVENT_APPS } from '@/config/constants';
import { getFieldLabel, getFieldGroupLabel } from '../field-labels';

interface PickListValue {
  value: string;
  label: string;
}

interface FieldInputProps {
  value: string;
  onChange: (value: string) => void;
  availableFields: EventFieldPath[];
  pickListValues?: PickListValue[];
  placeholder?: string;
  className?: string;
}

// ── Data attribute used on badge spans to store the raw field reference ──
const BADGE_ATTR = 'data-field-raw';

/**
 * ContentEditable field input that supports:
 * - Direct text typing with visible cursor
 * - Inline non-editable badges for field references and picklist values
 * - # autocomplete for event fields
 * - Picklist dropdown on focus
 * - Drag & drop of event fields
 * - Atomic badge deletion (backspace/delete removes whole badge)
 */
export function FieldInput({
  value,
  onChange,
  availableFields,
  pickListValues = [],
  placeholder = 'Type value or press # for fields...',
  className,
}: FieldInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [isDragHover, setIsDragHover] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 280 });

  // Dropdown mode: 'fields' = # autocomplete, 'picklist' = picklist options on focus
  const [dropdownMode, setDropdownMode] = useState<'fields' | 'picklist'>('picklist');
  // Suppress re-opening picklist after a selection
  const suppressPicklistRef = useRef(false);
  // Track whether we're doing a programmatic DOM update to skip input handler
  const isUpdatingRef = useRef(false);
  // Track the last value we serialized to avoid cycles
  const lastSerializedRef = useRef(value);

  const hasPicklist = pickListValues.length > 0;

  // Event apps (Calendly, Acuity) label person fields by owner: "Invitee Email" vs "Host Email"
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);

  // Path → label, so pills read "Address - Answer" instead of a bare "Answer"
  const fieldLabels = useMemo(() => {
    const labels = new Map<string, string>();
    availableFields.forEach((f) => {
      if (f.label) labels.set(f.path, f.label);
    });
    return labels;
  }, [availableFields]);

  const labelFor = useCallback(
    (path: string) => fieldLabels.get(path) ?? getFieldLabel(path, isEventApp),
    [fieldLabels, isEventApp]
  );

  // Filter event fields based on text after the last #
  const filteredFields = useMemo(() => {
    if (!searchFilter) return availableFields;
    const lower = searchFilter.toLowerCase();
    return availableFields.filter(
      (f) =>
        f.path.toLowerCase().includes(lower) ||
        labelFor(f.path).toLowerCase().includes(lower) ||
        String(f.sampleValue).toLowerCase().includes(lower)
    );
  }, [availableFields, searchFilter, labelFor]);

  // Group fields by top-level parent for the dropdown
  const groupedFields = useMemo(() => {
    const groups: Record<string, EventFieldPath[]> = {};
    const sliced = filteredFields.slice(0, 30);

    sliced.forEach((field) => {
      const parts = field.path.replace(/^\$\./, '').split('.');
      const group = parts.length > 1 ? parts[0] : '$';
      if (!groups[group]) groups[group] = [];
      groups[group].push(field);
    });

    return groups;
  }, [filteredFields]);

  // Filter picklist values based on current input value (as search)
  const filteredPicklist = useMemo(() => {
    if (!hasPicklist) return [];
    if (!value || pickListValues.some((p) => p.value === value)) return pickListValues;
    const lower = value.toLowerCase();
    return pickListValues.filter(
      (p) =>
        p.label.toLowerCase().includes(lower) ||
        p.value.toLowerCase().includes(lower)
    );
  }, [hasPicklist, pickListValues, value]);

  // ── Build HTML content from raw value string ──
  const buildEditorHTML = useCallback(
    (val: string): string => {
      if (!val) return '';

      // Check if entire value matches a picklist option
      if (hasPicklist) {
        const matchedPick = pickListValues.find((p) => p.value === val);
        if (matchedPick) {
          return makeBadgeHTML(matchedPick.label, val);
        }
      }

      // Parse field references: ${{{...}}}
      const refRegex = /(\$\{\{\{(.+?)\}\}\})/g;
      let result = '';
      let lastIndex = 0;
      let match;

      while ((match = refRegex.exec(val)) !== null) {
        // Plain text before match
        if (match.index > lastIndex) {
          result += escapeHTML(val.substring(lastIndex, match.index));
        }
        // Badge for field reference
        const path = match[2];
        result += makeBadgeHTML(labelFor(path), match[1]);
        lastIndex = match.index + match[0].length;
      }

      // Remaining plain text
      if (lastIndex < val.length) {
        result += escapeHTML(val.substring(lastIndex));
      }

      return result;
    },
    [hasPicklist, pickListValues, labelFor]
  );

  // ── Serialize DOM content back to raw value string ──
  const serializeEditor = useCallback((): string => {
    const editor = editorRef.current;
    if (!editor) return '';

    let result = '';
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        // Check if it's a badge element
        const raw = el.getAttribute(BADGE_ATTR);
        if (raw) {
          result += raw;
        } else if (el.tagName === 'BR') {
          // Ignore line breaks in single-line input
        } else {
          // Recurse into child nodes
          el.childNodes.forEach(walk);
        }
      }
    };
    editor.childNodes.forEach(walk);
    // Strip zero-width spaces used as cursor spacers
    return result.replace(/\u200B/g, '');
  }, []);

  // ── Sync editor DOM when value prop changes externally ──
  useEffect(() => {
    if (isUpdatingRef.current) return;
    if (value === lastSerializedRef.current) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Save and restore selection if focused
    const sel = window.getSelection();
    const wasFocused = document.activeElement === editor;
    let savedOffset = -1;

    if (wasFocused && sel && sel.rangeCount > 0) {
      // Try to save a rough cursor offset
      const range = sel.getRangeAt(0);
      savedOffset = getCaretOffset(editor, range);
    }

    isUpdatingRef.current = true;
    editor.innerHTML = buildEditorHTML(value);
    lastSerializedRef.current = value;
    isUpdatingRef.current = false;

    if (wasFocused && savedOffset >= 0) {
      requestAnimationFrame(() => {
        restoreCaretOffset(editor, savedOffset);
      });
    }
  }, [value, buildEditorHTML]);

  // ── Initial render ──
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    isUpdatingRef.current = true;
    editor.innerHTML = buildEditorHTML(value);
    lastSerializedRef.current = value;
    isUpdatingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-label pills once event fields load (they often arrive after mount) ──
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    isUpdatingRef.current = true;
    editor.innerHTML = buildEditorHTML(lastSerializedRef.current);
    isUpdatingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldLabels]);

  // Update dropdown position when it opens
  useEffect(() => {
    if (!showDropdown || !editorRef.current) return;
    const rect = editorRef.current.getBoundingClientRect();
    const wanted = dropdownMode === 'picklist' ? 240 : 280;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < wanted && spaceAbove > spaceBelow;
    const maxHeight = Math.min(wanted, openUp? spaceAbove : spaceBelow);

    setDropdownPos({
      top: openUp ? rect.top - 4 - maxHeight : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
      maxHeight,
    });
  }, [showDropdown, searchFilter, value, dropdownMode]);

  // ── Handle contentEditable input ──
  const handleInput = useCallback(() => {
    if (isUpdatingRef.current) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Serialize the DOM content to raw value
    const newValue = serializeEditor();
    lastSerializedRef.current = newValue;
    onChange(newValue);

    // Clean up leftover <br> / empty nodes so :empty CSS placeholder works
    if (!newValue && editor.innerHTML !== '') {
      editor.innerHTML = '';
    }

    // Check for # autocomplete trigger
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;
      if (textNode.nodeType === Node.TEXT_NODE) {
        const text = textNode.textContent || '';
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const lastHash = textBeforeCursor.lastIndexOf('#');

        if (lastHash !== -1) {
          const textAfterHash = textBeforeCursor.substring(lastHash + 1);
          if (!textAfterHash.includes(' ')) {
            setSearchFilter(textAfterHash);
            setDropdownMode('fields');
            setShowDropdown(true);
            setHighlightIndex(0);
            return;
          }
        }
      }
    }

    // If was in fields mode but hash is gone, close
    if (dropdownMode === 'fields') {
      setShowDropdown(false);
      setSearchFilter('');
    }
  }, [serializeEditor, onChange, dropdownMode]);

  // ── Handle focus ──
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (suppressPicklistRef.current) {
      suppressPicklistRef.current = false;
      return;
    }
    if (hasPicklist) {
      setDropdownMode('picklist');
      setShowDropdown(true);
      setHighlightIndex(0);
    }
  }, [hasPicklist]);

  // ── Handle blur ──
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Delay dropdown close to allow click on dropdown items
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        setSearchFilter('');
      }
    }, 150);
  }, []);

  // ── Insert a field reference badge at current cursor ──
  const insertFieldReference = useCallback(
    (fieldPath: string) => {
      const editor = editorRef.current;
      if (!editor) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        // No selection — append
        const raw = `\${{{${fieldPath}}}}`;
        const newValue = value + raw;
        onChange(newValue);
        setShowDropdown(false);
        setSearchFilter('');
        return;
      }

      // Find and remove the # trigger text
      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;

      if (textNode.nodeType === Node.TEXT_NODE) {
        const text = textNode.textContent || '';
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const lastHash = textBeforeCursor.lastIndexOf('#');

        if (lastHash !== -1) {
          // Remove # and filter text
          const beforeHash = text.substring(0, lastHash);
          const afterCursor = text.substring(cursorPos);
          textNode.textContent = beforeHash + afterCursor;

          // Create and insert badge
          const badge = createBadgeElement(
            labelFor(fieldPath),
            `\${{{${fieldPath}}}}`
          );

          // Position for insertion
          const insertRange = document.createRange();
          insertRange.setStart(textNode, lastHash);
          insertRange.collapse(true);
          insertRange.insertNode(badge);

          // Place cursor after the badge
          const afterRange = document.createRange();
          afterRange.setStartAfter(badge);
          afterRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(afterRange);
        }
      }

      // Serialize and update
      isUpdatingRef.current = true;
      const newValue = serializeEditor();
      lastSerializedRef.current = newValue;
      onChange(newValue);
      isUpdatingRef.current = false;

      setShowDropdown(false);
      setSearchFilter('');
    },
    [value, onChange, serializeEditor, labelFor]
  );

  // ── Select a picklist value ──
  const handlePicklistSelect = useCallback(
    (pickValue: string) => {
      onChange(pickValue);
      setShowDropdown(false);
      suppressPicklistRef.current = true;

      // Update editor content
      const editor = editorRef.current;
      if (editor) {
        isUpdatingRef.current = true;
        editor.innerHTML = buildEditorHTML(pickValue);
        lastSerializedRef.current = pickValue;
        isUpdatingRef.current = false;
      }

      setTimeout(() => editor?.focus(), 0);
    },
    [onChange, buildEditorHTML]
  );

  // ── Keyboard navigation ──
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Prevent Enter from creating new lines
      if (e.key === 'Enter' && !showDropdown) {
        e.preventDefault();
        return;
      }

      if (!showDropdown) return;

      if (dropdownMode === 'fields') {
        const flatList = filteredFields.slice(0, 30);

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev < flatList.length - 1 ? prev + 1 : 0
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev > 0 ? prev - 1 : flatList.length - 1
            );
            break;
          case 'Enter':
            e.preventDefault();
            if (flatList[highlightIndex]) {
              insertFieldReference(flatList[highlightIndex].path);
            }
            break;
          case 'Escape':
            e.preventDefault();
            setShowDropdown(false);
            setSearchFilter('');
            break;
        }
      } else if (dropdownMode === 'picklist') {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev < filteredPicklist.length - 1 ? prev + 1 : 0
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev > 0 ? prev - 1 : filteredPicklist.length - 1
            );
            break;
          case 'Enter':
            e.preventDefault();
            if (filteredPicklist[highlightIndex]) {
              handlePicklistSelect(filteredPicklist[highlightIndex].value);
            }
            break;
          case 'Escape':
            e.preventDefault();
            setShowDropdown(false);
            break;
        }
      }
    },
    [
      showDropdown,
      dropdownMode,
      filteredFields,
      highlightIndex,
      insertFieldReference,
      filteredPicklist,
      handlePicklistSelect,
    ]
  );

  // ── Handle paste — strip HTML, insert plain text only ──
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      // Insert as plain text
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        // Move cursor to end of inserted text
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // Trigger input handler
      handleInput();
    },
    [handleInput]
  );

  // ── Handle drag & drop ──
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragHover(false);

      const fieldPath = e.dataTransfer.getData('application/x-field-path');
      if (!fieldPath) return;

      const editor = editorRef.current;
      if (!editor) return;

      // Build badge and insert into the editor DOM directly
      const fieldReference = `\${{{${fieldPath}}}}`;
      const badge = createBadgeElement(
        labelFor(fieldPath),
        fieldReference
      );

      // Append badge at end of editor content
      editor.appendChild(badge);

      // Add a zero-width space after the badge so cursor can be placed after it
      const spacer = document.createTextNode('\u200B');
      editor.appendChild(spacer);

      // Move cursor after the badge
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.setStartAfter(spacer);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      // Serialize and update value
      isUpdatingRef.current = true;
      const newValue = serializeEditor();
      lastSerializedRef.current = newValue;
      onChange(newValue);
      isUpdatingRef.current = false;

      // Focus the editor
      editor.focus();
    },
    [onChange, serializeEditor, labelFor]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Must prevent default to allow drop on contentEditable
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('application/x-field-path')) {
      setIsDragHover(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if we're actually leaving the editor, not entering a child
    const editor = editorRef.current;
    if (editor && !editor.contains(e.relatedTarget as Node)) {
      setIsDragHover(false);
    }
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        editorRef.current &&
        !editorRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setSearchFilter('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Close dropdown on scroll of parent
  useEffect(() => {
    if (!showDropdown || !editorRef.current) return;
    const scrollParent = editorRef.current.closest('[class*="overflow"]');
    if (!scrollParent) return;

    const handleScroll = () => {
      setShowDropdown(false);
      setSearchFilter('');
    };
    scrollParent.addEventListener('scroll', handleScroll);
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [showDropdown]);

  // Track flat index across grouped items
  let flatIdx = 0;

  // Determine what to show in dropdown
  const showFieldsDropdown = showDropdown && dropdownMode === 'fields' && filteredFields.length > 0;
  const showPicklistDropdown = showDropdown && dropdownMode === 'picklist' && filteredPicklist.length > 0;

  return (
    <div className="relative">
      {/* ── ContentEditable input ── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        autoCorrect="off"
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        data-placeholder={isDragHover ? 'Drop event field here...' : placeholder}
        className={`field-input-editable w-full rounded-md border bg-background px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary whitespace-nowrap overflow-hidden ${
          isDragHover
            ? 'border-dashed border-primary ring-1 ring-primary/30 bg-primary/5'
            : 'border-border'
        } ${className || ''}`}
      />

      {/* ── Picklist options dropdown ── */}
      {showPicklistDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed max-h-[240px] overflow-y-auto rounded-lg border border-border bg-background shadow-xl animate-fade-in"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
              zIndex: 9999,
            }}
          >
            {filteredPicklist.map((opt, idx) => (
              <button
                key={opt.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePicklistSelect(opt.value);
                }}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] transition-colors ${
                  idx === highlightIndex
                    ? 'bg-primary/10 text-foreground'
                    : opt.value === value
                      ? 'bg-muted/50 text-foreground font-medium'
                      : 'text-foreground hover:bg-muted/50'
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {opt.value !== opt.label && (
                  <span className="ml-2 shrink-0 text-[9px] text-muted-foreground/40 font-mono">
                    {opt.value}
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body
        )}

      {/* ── Event fields autocomplete dropdown (#) ── */}
      {showFieldsDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed max-h-[280px] overflow-y-auto rounded-lg border border-border bg-background shadow-xl animate-fade-in"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
              zIndex: 9999,
            }}
          >
            {Object.entries(groupedFields).map(([groupName, fields]) => (
              <div key={groupName}>
                {/* Group header */}
                {Object.keys(groupedFields).length > 1 && (
                  <div className="sticky top-0 bg-muted/80 px-3 py-1 backdrop-blur-sm">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                      {getFieldGroupLabel(groupName, isEventApp)}
                    </span>
                  </div>
                )}

                {fields.map((field) => {
                  const currentFlatIdx = flatIdx++;
                  const humanName = labelFor(field.path);

                  return (
                    <button
                      key={field.path}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertFieldReference(field.path);
                      }}
                      className={`flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors ${
                        currentFlatIdx === highlightIndex
                          ? 'bg-primary/10 text-foreground'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-foreground">
                          {humanName}
                        </p>
                        <p className="truncate text-[10px] font-mono text-muted-foreground/60">
                          {field.path}
                        </p>
                      </div>
                      <span className="shrink-0 truncate max-w-[120px] text-[10px] text-muted-foreground/50">
                        {formatSample(field.sampleValue)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// ── Helpers ──

function formatSample(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'string') {
    return value.length > 30 ? value.substring(0, 30) + '...' : value;
  }
  return String(value);
}

function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Create badge HTML string for innerHTML insertion */
function makeBadgeHTML(label: string, rawValue: string): string {
  const escapedRaw = rawValue.replace(/"/g, '&quot;');
  const escapedLabel = escapeHTML(label);
  return `<span contenteditable="false" ${BADGE_ATTR}="${escapedRaw}" class="field-input-badge">${escapedLabel}</span>`;
}

/** Create a badge DOM element */
function createBadgeElement(label: string, rawValue: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.contentEditable = 'false';
  span.setAttribute(BADGE_ATTR, rawValue);
  span.className = 'field-input-badge';
  span.textContent = label;
  return span;
}

/** Get a rough character offset of the caret within the editor */
function getCaretOffset(editor: HTMLElement, range: Range): number {
  const preRange = document.createRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
}

/** Restore caret to a character offset within the editor */
function restoreCaretOffset(editor: HTMLElement, offset: number): void {
  const sel = window.getSelection();
  if (!sel) return;

  let currentOffset = 0;
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const len = (node.textContent || '').length;
    if (currentOffset + len >= offset) {
      const range = document.createRange();
      range.setStart(node, Math.min(offset - currentOffset, len));
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    currentOffset += len;
  }

  // If offset is beyond end, place at end
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}
